import { useState, type ReactElement } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '@/api/client';
import { useM3Mutation, useRegistration } from '@/api/m3';
import type { ChecklistKey, RegistrationCard, RegistrationRowDto } from '@/api/m3-types';
import { ActionMenu, RowActions } from '@/components/kit/ActionMenu';
import { Button } from '@/components/kit/Button';
import { ConditionItem, ConditionList, Switch, TextLink } from '@/components/kit/Controls';
import { Breadcrumbs, Fact, Facts, Kv } from '@/components/kit/Layout';
import { MetricRow } from '@/components/kit/MetricRow';
import { Alert, ErrorState, Note, Skeleton } from '@/components/kit/Note';
import { ObjectTabs } from '@/components/kit/ObjectTabs';
import { Overview, Panel, Signals } from '@/components/kit/Panel';
import { PersonHeader } from '@/components/kit/Person';
import { Pill } from '@/components/kit/Pill';
import { PopoverSelect } from '@/components/kit/Popover';
import { useToast } from '@/components/kit/Toast';
import { date as ruDate, money, mmss, people } from '@/lib/format';
import { formatPhone, years } from '@/lib/phone';
import { errorText } from '@/features/base/shared/useUndoable';
import { dateTime, daysText, initialsOf, nums, READINESS, REG_STATUS, stageTitle } from '@/features/crm/labels';
import { M3Feed, TasksStub } from '@/features/crm/M3Feed';
import { useM3Action } from '@/features/crm/useM3Submit';
import {
  DiscountModal,
  ConfirmModal,
  DeclineModal,
  MoveModal,
  MusicModal,
  PayModal,
  PickFromTeamModal,
  RefundModal,
  WithdrawModal,
} from './RegDialogs';

type Tab = 'ov' | 'rows' | 'bill';
type Dialog =
  | { kind: 'pay' | 'refund' | 'adj' | 'decline' | 'move' | 'music' | 'reserve' | 'pick' }
  | { kind: 'pickRow' | 'withdraw' | 'removeRow'; row: RegistrationRowDto };

const METHOD = { link: 'Платёжная ссылка', transfer: 'Перевод', cash: 'Наличные', carry: 'Зачёт с переноса' } as const;

/** Пункты чек-листа: кто отмечает и чей срок (REQ в макете) */
const ITEMS: { key: ChecklistKey; title: string; who: string }[] = [
  { key: 'comp', title: 'Состав', who: 'менеджер' },
  { key: 'pay', title: 'Оплата', who: 'система по платежам' },
  { key: 'music', title: 'Музыка', who: 'менеджер' },
  { key: 'video', title: 'Видео', who: 'менеджер' },
  { key: 'costume', title: 'Костюмы', who: 'менеджер' },
  { key: 'admission', title: 'Допуск', who: 'менеджер' },
];

const STATE_CLASS = { ok: 'done', wait: 'wait', none: 'none', na: 'na' } as const;
const periodText = (p: number) => (p === 1 ? 'первая цена' : 'вторая цена');
const dm = (iso: string) => ruDate(iso).slice(0, 5);

/**
 * Карточка номера (viewReg в макете, ТЗ 5.4–5.6): паспорт, сигналы,
 * цифры, вкладки «Обзор · Участники · Счёт и платежи».
 */
export function RegistrationPage() {
  const { registrationId } = useParams();
  const navigate = useNavigate();
  const card = useRegistration(registrationId);
  const [tab, setTab] = useState<Tab>('ov');
  const [dialog, setDialog] = useState<Dialog | null>(null);
  const close = () => setDialog(null);

  if (card.isError) {
    return (
      <div className="main">
        <Breadcrumbs items={[{ label: 'Регистрации', onClick: () => navigate('/registrations') }, { label: 'Номер' }]} />
        <ErrorState message={card.error.message} onRetry={() => void card.refetch()} />
      </div>
    );
  }
  if (card.isLoading || !card.data) {
    return (
      <div className="main">
        <Breadcrumbs items={[{ label: 'Регистрации', onClick: () => navigate('/registrations') }, { label: '…' }]} />
        <Skeleton width={320} height={28} />
        <div className="sigs" />
        <Skeleton width="60%" />
      </div>
    );
  }

  const x = card.data;
  const open = x.status === 'active' || x.status === 'reserve';
  return (
    <div className="main has-abar">
      <Breadcrumbs
        items={[
          { label: 'Регистрации', onClick: () => navigate('/registrations') },
          { label: x.event.label, onClick: () => navigate(`/registrations?eventId=${x.event.id}`) },
          { label: `${x.who} · ${x.nomination.label}` },
        ]}
      />
      <RegHeader reg={x} onDialog={setDialog} />
      <Passport reg={x} />
      <RegSignals reg={x} onDialog={setDialog} />
      <MetricRow
        variant="sum"
        className="m3"
        items={[
          {
            label: 'Участников',
            value: x.liveRows,
            sub: `${x.withdrawnRows ? `снято ${x.withdrawnRows} · ` : ''}${x.unfilledRows ? `не заполнено ${x.unfilledRows}` : 'все вписаны'}`,
          },
          {
            label: 'План',
            value: x.status === 'active' ? money(x.figures.billed) : '—',
            sub:
              x.status === 'active'
                ? `${periodText(x.price.fixed ? (x.price.period ?? 1) : x.price.currentPeriod)}${x.price.fixed ? ' · зафикс.' : ''}`
                : 'счёта нет',
          },
          { label: 'Факт', value: money(x.figures.paid), sub: 'оплачено' },
          { label: 'Долг', value: money(x.figures.debt) },
        ]}
      />
      <ObjectTabs
        value={tab}
        onChange={setTab}
        items={[
          { value: 'ov', label: 'Обзор' },
          { value: 'rows', label: 'Участники' },
          { value: 'bill', label: 'Счёт и платежи' },
        ]}
      />
      {tab === 'ov' ? (
        <Overview
          left={
            <>
              <ChecklistPanel reg={x} onMusic={() => setDialog({ kind: 'music' })} />
              <NominationPanel reg={x} />
            </>
          }
          right={
            <>
              <TasksStub of="номеру" />
              <M3Feed scope="registrations" id={x.id} />
            </>
          }
        />
      ) : null}
      {tab === 'rows' ? <RowsPanel reg={x} open={open} onDialog={setDialog} /> : null}
      {tab === 'bill' ? <BillTab reg={x} onDialog={setDialog} /> : null}

      {dialog?.kind === 'pay' ? <PayModal reg={x} onClose={close} /> : null}
      {dialog?.kind === 'refund' ? <RefundModal reg={x} onClose={close} /> : null}
      {dialog?.kind === 'adj' ? <DiscountModal reg={x} onClose={close} /> : null}
      {dialog?.kind === 'decline' ? <DeclineModal reg={x} onClose={close} /> : null}
      {dialog?.kind === 'move' ? <MoveModal reg={x} onClose={close} /> : null}
      {dialog?.kind === 'music' ? <MusicModal reg={x} onClose={close} /> : null}
      {dialog?.kind === 'pick' ? <PickFromTeamModal reg={x} onClose={close} /> : null}
      {dialog?.kind === 'pickRow' ? <PickFromTeamModal reg={x} row={dialog.row} onClose={close} /> : null}
      {dialog?.kind === 'withdraw' ? <WithdrawModal reg={x} row={dialog.row} onClose={close} /> : null}
      {dialog?.kind === 'reserve' ? (
        <ConfirmModal
          title="Перевести в резерв?"
          text={`Номер «${x.who}» перестанет участвовать: счёт снимется${x.figures.paid ? `, оплаченные ${money(x.figures.paid)} уйдут к возврату` : ''}. Вернуть можно в любой момент — «Поднять из резерва».`}
          label="Перевести в резерв"
          run={() => api.post(`/registrations/${x.id}/reserve`)}
          done={`Номер в резерве${x.figures.paid ? ', оплаченное — к возврату' : ''}.`}
          onClose={close}
        />
      ) : null}
      {dialog?.kind === 'removeRow' ? (
        <ConfirmModal
          title="Убрать строку?"
          text={`Строка ${dialog.row.position} удалится без следа — только для строки, заведённой по ошибке. Участника, который не выступит, снимают с причиной.`}
          label="Убрать строку"
          run={() => api.delete(`/registration-rows/${dialog.row.id}`)}
          done="Строка убрана, счёт пересчитан"
          onClose={close}
        />
      ) : null}
    </div>
  );
}

function RegHeader({ reg: x, onDialog }: { reg: RegistrationCard; onDialog: (d: Dialog) => void }) {
  const navigate = useNavigate();
  const action = useM3Action();
  const status = REG_STATUS[x.status];
  const ready = x.status === 'active' ? READINESS[x.readiness] : null;
  let main = null;
  let extra: { label: string; danger?: boolean; onClick: () => void }[] = [];
  if (x.status === 'active') {
    main = (
      <Button
        tone="primary"
        disabled={x.event.draft}
        title={x.event.draft ? 'Оплата откроется после публикации мероприятия' : undefined}
        onClick={() => onDialog({ kind: 'pay' })}
      >
        Внести платёж
      </Button>
    );
    extra = [
      { label: 'Перенести на другое мероприятие…', onClick: () => onDialog({ kind: 'move' }) },
      { label: 'Перевести в резерв…', onClick: () => onDialog({ kind: 'reserve' }) },
      { label: 'Оформить отказ…', danger: true, onClick: () => onDialog({ kind: 'decline' }) },
    ];
  }
  if (x.status === 'reserve') {
    main = (
      <Button
        tone="primary"
        disabled={action.pending}
        onClick={() => action.run(() => api.post(`/registrations/${x.id}/promote`), `«${x.who}» поднята из резерва, счёт выставлен`)}
      >
        Поднять из резерва
      </Button>
    );
    extra = [
      { label: 'Перенести…', onClick: () => onDialog({ kind: 'move' }) },
      { label: 'Оформить отказ…', danger: true, onClick: () => onDialog({ kind: 'decline' }) },
    ];
  }
  if (x.status === 'declined') {
    main = (
      <Button
        tone="primary"
        disabled={action.pending}
        onClick={() =>
          action.run(
            () => api.post<{ status: string }>(`/registrations/${x.id}/undecline`),
            (r) => (r.status === 'active' ? 'Номер снова участвует' : 'Номинация закрыта — номер встал в резерв'),
          )
        }
      >
        Вернуть номер
      </Button>
    );
  }
  return (
    <PersonHeader
      initials={initialsOf(x.who)}
      title={x.who}
      pills={
        <>
          <Pill tone={status.tone}>{status.label}</Pill>
          {ready ? <Pill tone={ready.tone}>{ready.label}</Pill> : null}
        </>
      }
      actions={
        <>
          <Button onClick={() => navigate(`/crm/deals/${x.deal.id}`)}>Открыть сделку</Button>
          {main}
          {extra.length > 2 ? (
            <ActionMenu items={extra} />
          ) : (
            extra.map((i) => (
              <Button key={i.label} onClick={i.onClick}>
                {i.label}
              </Button>
            ))
          )}
        </>
      }
    />
  );
}

function Passport({ reg: x }: { reg: RegistrationCard }) {
  const navigate = useNavigate();
  const at = dateTime(x.createdAt);
  const slot = x.nomination.slot;
  const [y, m1, d1] = x.event.dateFrom.split('-');
  const [, m2, d2] = x.event.dateTo.split('-');
  return (
    <div className="panel pass">
      <Facts columns={5}>
        <Fact label="Мероприятие" sub={x.event.dateFrom === x.event.dateTo ? `${d1}.${m1}.${y}` : `${d1}.${m1} — ${d2}.${m2}.${y}`}>
          {x.event.city}
        </Fact>
        <Fact label="Номинация" sub={slot ? [dm(slot.date), slot.blockName, slot.start].filter(Boolean).join(' · ') : 'вне программы'}>
          {x.nomination.label}
        </Fact>
        <Fact label={x.soloist ? 'Солист' : 'Руководитель'} sub={x.client.phone ? <span className="num">{formatPhone(x.client.phone)}</span> : undefined}>
          <span className="lnk" onClick={() => navigate(`/clients/${x.client.id}`)}>
            {x.client.shortName}
          </span>
        </Fact>
        <Fact label="Дата регистрации" sub={at.time}>
          <span className="num">{ruDate(x.createdAt.slice(0, 10))}</span>
        </Fact>
        <Fact label="Сделка" sub={`${nums(x.deal.activeCount)} в сделке`}>
          <span className="lnk" onClick={() => navigate(`/crm/deals/${x.deal.id}`)}>
            {stageTitle(x.deal.stage)}
          </span>
        </Fact>
      </Facts>
    </div>
  );
}

function RegSignals({ reg: x, onDialog }: { reg: RegistrationCard; onDialog: (d: Dialog) => void }) {
  const navigate = useNavigate();
  const action = useM3Action();
  const def = x.price.defaultPackage;
  const raiseIn =
    !x.price.fixed && x.status === 'active' && x.price.currentPeriod === 1 && def?.raiseOn && def.priceSecond
      ? Math.round((new Date(`${def.raiseOn}T00:00:00`).getTime() - new Date(new Date().toDateString()).getTime()) / 86_400_000)
      : null;
  return (
    <Signals>
      {x.event.draft && x.status === 'active' ? (
        <Alert tone="info">
          <b>Мероприятие ещё не опубликовано.</b>
          <span>Номер заведён и считается в план; оплата откроется после публикации.</span>
        </Alert>
      ) : null}
      {x.figures.noSeats ? (
        <Alert action={<Button small onClick={() => onDialog({ kind: 'reserve' })}>В резерв</Button>}>
          <b>
            Лимит набран: оплатили {x.nomination.paidSeats} из {x.nomination.limit}.
          </b>
          <span>Платёж по этому номеру выведет номинацию сверх лимита. Это предупреждение — решите сами: принять, перевести в резерв или перенести.</span>
        </Alert>
      ) : null}
      {x.figures.nominationOverLimit ? (
        <Alert tone="crit">
          <b>
            Номинация сверх лимита: оплатили {x.nomination.paidSeats} из {x.nomination.limit} мест.
          </b>
          <span>Поднимите лимит номинации в мероприятии или переведите номер в резерв — платёж уйдёт в возврат.</span>
        </Alert>
      ) : null}
      {x.figures.toRefund ? (
        <Alert action={<Button small onClick={() => onDialog({ kind: 'refund' })}>Отметить возврат</Button>}>
          <b>К возврату {money(x.figures.toRefund)}.</b>
          <span>
            {x.status !== 'active'
              ? 'Номер не участвует, оплаченное возвращается.'
              : x.withdrawnRows
                ? 'С номера снят участник — оплаченное за него возвращается полностью.'
                : 'Оплачено больше начисленного.'}
          </span>
        </Alert>
      ) : null}
      {raiseIn !== null && raiseIn >= 0 && raiseIn <= 7 && def ? (
        <Alert tone="mut">
          <b>{raiseIn ? `Через ${daysText(raiseIn)} цена вырастет.` : 'Завтра цена вырастет.'}</b>
          <span>
            {def.name}: {money(def.priceFirst)} → {money(def.priceSecond ?? def.priceFirst)} за участника. Цена фиксируется
            первым платежом по номеру.
          </span>
        </Alert>
      ) : null}
      {x.status === 'reserve' ? (
        <Alert
          tone="info"
          action={
            // Поднять из резерва менеджер может всегда: лимит справочный (БЛ 5.16)
            <Button small disabled={action.pending} onClick={() => action.run(() => api.post(`/registrations/${x.id}/promote`), 'Поднята из резерва, счёт выставлен')}>
              Поднять из резерва
            </Button>
          }
        >
          <b>В резерве с {x.reserveSince ? ruDate(x.reserveSince) : '—'}.</b>
          <span>
            Свободно мест: {x.nomination.free} из {x.nomination.limit}. Счёт не выставлен, в выручку не входит.
          </span>
        </Alert>
      ) : null}
      {x.decline ? (
        <Alert tone="mut">
          <b>Отказ {ruDate(x.decline.at)}.</b>
          <span>
            {x.decline.reason}
            {x.decline.by ? ` · оформил(а) ${x.decline.by}` : ''}
          </span>
        </Alert>
      ) : null}
      {x.movedTo ? (
        <Alert tone="mut" action={<Button small onClick={() => navigate(`/registrations/${x.movedTo!.id}`)}>Открыть новую регистрацию</Button>}>
          <b>Перенесена в «{x.movedTo.event}».</b>
          <span>{x.carried ? 'Деньги ушли зачётом, место здесь освободилось.' : 'Место здесь освободилось.'} Это не отказ.</span>
        </Alert>
      ) : null}
      {x.movedFrom ? (
        <Alert tone="mut" action={<Button small onClick={() => navigate(`/registrations/${x.movedFrom!.id}`)}>Исходная запись</Button>}>
          <b>Перенесена из «{x.movedFrom.event}».</b>
          <span>
            {x.movedFrom.nomination}
            {x.carried ? ` · зачтено ${money(x.carried)}` : ''}
          </span>
        </Alert>
      ) : null}
    </Signals>
  );
}

function ChecklistPanel({ reg: x, onMusic }: { reg: RegistrationCard; onMusic: () => void }) {
  const toast = useToast();
  const mark = useM3Mutation((body: Record<string, unknown>) => api.post(`/registrations/${x.id}/checklist`, body));
  const run = (body: Record<string, unknown>, done: string) =>
    mark.mutate(body, { onSuccess: () => toast.show(done), onError: (e) => toast.showError(errorText(e)) });
  const active = x.status === 'active';
  const others = x.checklist.filter((c) => c.key !== 'admission').every((c) => c.state === 'ok' || c.state === 'na');

  const line = (key: ChecklistKey, state: string): { hint: string; action?: ReactElement } => {
    const small = (label: string, body: Record<string, unknown>, done: string, ghost?: boolean) => (
      <Button small tone={ghost ? 'ghost' : 'quiet'} disabled={mark.isPending} onClick={() => run(body, done)}>
        {label}
      </Button>
    );
    switch (key) {
      case 'comp':
        return {
          hint:
            state === 'ok'
              ? `состав утверждён, строки заблокированы${x.compWarnings.length ? ` · ${x.compWarnings.join(' · ')}` : ''}`
              : x.compWarnings.length
                ? x.compWarnings.join(' · ')
                : 'ждём подтверждения состава',
          action:
            state === 'ok'
              ? small('Разблокировать состав', { item: 'comp', status: 'wait' }, 'Состав разблокирован — строки снова можно править', true)
              : small('Утвердить состав', { item: 'comp', status: 'ok' }, 'Состав утверждён, строки заблокированы'),
        };
      case 'pay':
        return {
          hint: !active
            ? 'номер не участвует'
            : state === 'ok'
              ? 'оплачено полностью'
              : state === 'wait'
                ? `оплачено ${money(x.figures.paid)} из ${money(x.figures.billed)}`
                : 'платежей нет',
        };
      case 'music': {
        const over = x.music.sec !== null ? x.music.sec - x.nomination.durationLimitSec : 0;
        return {
          hint:
            x.music.sec === null
              ? 'фонограммы нет'
              : `${mmss(x.music.sec)} при лимите ${mmss(x.nomination.durationLimitSec)}${over > 0 ? ` · превышение ${over} с` : ''}${x.music.confirmed ? ' · подтверждена' : ' · не подтверждена'}`,
          action: (
            <Button small onClick={onMusic}>
              {x.music.sec === null ? 'Внести длительность' : 'Изменить'}
            </Button>
          ),
        };
      }
      case 'video':
      case 'costume': {
        const o = key === 'video' ? x.video : x.costume;
        const what = key === 'video' ? 'видео' : 'фото костюмов';
        return {
          hint:
            state === 'na'
              ? 'не требуется в этом номере'
              : !o.received
                ? `${what} не получено`
                : o.confirmed
                  ? 'получено и подтверждено'
                  : 'получено, ждёт подтверждения',
          action:
            state === 'na'
              ? undefined
              : !o.received
                ? small('Получено', { item: key, received: true }, 'Отмечено: получено')
                : !o.confirmed
                  ? small('Подтвердить', { item: key, received: true, confirmed: true }, 'Подтверждено')
                  : undefined,
        };
      }
      case 'admission':
        return {
          hint: x.admittedAt ? 'номер допущен к выступлению' : others ? 'все пункты выполнены' : 'ждём остальные пункты',
          action: x.admittedAt
            ? small('Снять допуск', { item: 'admission', admitted: false }, 'Допуск снят', true)
            : others
              ? small('Допустить', { item: 'admission', admitted: true }, 'Номер допущен')
              : undefined,
        };
    }
  };

  return (
    <Panel title="Чек-лист готовности">
      <ConditionList reqs>
        {ITEMS.map((it) => {
          const c = x.checklist.find((v) => v.key === it.key)!;
          const { hint, action } = line(it.key, c.state);
          return (
            <ConditionItem
              key={it.key}
              state={STATE_CLASS[c.state]}
              name={it.title}
              hint={
                <>
                  {hint}
                  {c.deadline && c.state !== 'na' ? (
                    <>
                      {' '}
                      · срок {dm(c.deadline)}
                      {c.overdue ? <span className="ctx"> · просрочено</span> : null}
                    </>
                  ) : null}{' '}
                  · отмечает {it.who}
                </>
              }
              action={active ? action : undefined}
            />
          );
        })}
      </ConditionList>
    </Panel>
  );
}

function NominationPanel({ reg: x }: { reg: RegistrationCard }) {
  const toast = useToast();
  const req = useM3Mutation((body: Record<string, boolean | null>) => api.patch(`/registrations/${x.id}/requirements`, body));
  const toggle = (key: 'needVideo' | 'needCostume', next: boolean, nominationValue: boolean) =>
    req.mutate(
      // Совпало с номинацией — переопределение снимается
      { [key]: next === nominationValue ? null : next },
      { onSuccess: () => toast.show('Требования номера сохранены'), onError: (e) => toast.showError(errorText(e)) },
    );
  const N = x.nomination;
  const def = x.price.defaultPackage;
  const open = x.status === 'active' || x.status === 'reserve';
  return (
    <Panel title="Номинация">
      <Kv k="Номинация">
        {N.label}
        {N.isSolo ? <span className="muted"> · сольная</span> : null}
      </Kv>
      <Kv k="Выступление">
        {N.slot ? [dm(N.slot.date), N.slot.blockName, N.slot.start].filter(Boolean).join(' · ') : <span className="dim">номинации нет в программе</span>}
      </Kv>
      <Kv k="Лимит хронометража">{mmss(N.durationLimitSec)}</Kv>
      <Kv k="Места">
        оплатили {N.paidSeats} из {N.limit} мест{N.unpaid ? ` · без оплаты ${N.unpaid}` : ''}
      </Kv>
      {def ? (
        <Kv k="Пакет по умолчанию">
          {def.name} · {money(def.price)} <span className="muted">· сейчас {periodText(x.price.currentPeriod)}</span>
        </Kv>
      ) : null}
      <Kv k="Цена номера">
        {x.price.fixed ? (
          `зафиксирована ${ruDate(x.price.fixedOn)} · ${periodText(x.price.period ?? 1)}`
        ) : x.status === 'active' ? (
          `не зафиксирована · ${periodText(x.price.currentPeriod)}${x.price.currentPeriod === 1 && def?.raiseOn ? ` до ${ruDate(def.raiseOn)}` : ''}`
        ) : (
          <span className="na">—</span>
        )}
      </Kv>
      <hr className="hair" />
      <Switch
        checked={x.video.required}
        disabled={!open || req.isPending}
        onToggle={() => toggle('needVideo', !x.video.required, N.needVideo)}
        label={`Требуется видео · в номинации ${N.needVideo ? 'включено' : 'выключено'}`}
      />
      <Switch
        checked={x.costume.required}
        disabled={!open || req.isPending}
        onToggle={() => toggle('needCostume', !x.costume.required, N.needCostume)}
        label={`Требуются костюмы · в номинации ${N.needCostume ? 'включено' : 'выключено'}`}
      />
    </Panel>
  );
}

/** Поле строки участника: правится по месту, сохраняется по уходу с поля */
function CellText({ value, placeholder, onSave, narrow }: { value: string; placeholder: string; onSave: (v: string) => void; narrow?: boolean }) {
  const [v, setV] = useState(value);
  return (
    <input
      className={narrow ? 'cin d' : 'cin'}
      value={v}
      placeholder={placeholder}
      aria-label={placeholder}
      onChange={(e) => setV(e.target.value)}
      onBlur={() => {
        if (v.trim() !== value) onSave(v.trim());
      }}
    />
  );
}

/**
 * Телефон участника номера (Road Map п. 18). У строки с клиентом — из его
 * карточки, правится там; у строки без карточки — здесь, сервер приведёт
 * к +7. Совпал с клиентом базы — подсказка, сами не связываем.
 */
function RowPhone({
  row: r,
  canEdit,
  canLink,
  onSave,
  onLink,
}: {
  row: RegistrationRowDto;
  canEdit: boolean;
  canLink: boolean;
  onSave: (phone: string | null) => void;
  onLink: (clientId: string) => void;
}) {
  const m = r.phoneMatch;
  return (
    <>
      {canEdit ? (
        <CellText
          value={r.phone ? formatPhone(r.phone) : ''}
          placeholder="+7 (___) ___-__-__"
          onSave={(v) => onSave(v || null)}
        />
      ) : r.phone ? (
        <span className="num">{formatPhone(r.phone)}</span>
      ) : (
        <span className="na">—</span>
      )}
      {m ? (
        <div className="sub">
          Это {m.fullName}?{' '}
          {!m.inRoster ? (
            'Его нет в составе команды — включите в состав, тогда строку можно связать'
          ) : canLink ? (
            <TextLink onClick={() => onLink(m.clientId)}>Связать с клиентом</TextLink>
          ) : (
            'Чтобы связать, разблокируйте состав'
          )}
        </div>
      ) : null}
    </>
  );
}

function RowsPanel({ reg: x, open, onDialog }: { reg: RegistrationCard; open: boolean; onDialog: (d: Dialog) => void }) {
  const toast = useToast();
  const action = useM3Action();
  const patch = useM3Mutation((p: { id: string; body: Record<string, unknown> }) => api.patch(`/registration-rows/${p.id}`, p.body));
  const save = (id: string, body: Record<string, unknown>, done?: string) =>
    patch.mutate({ id, body }, { onSuccess: () => done && toast.show(done), onError: (e) => toast.showError(errorText(e)) });
  const live = x.rows.filter((r) => !r.withdrawn);
  const sum = live.reduce((a, r) => a + r.price, 0);
  // Утверждённый состав заблокирован (Road Map п. 16): правка строк — после разблокировки
  const locked = x.compStatus === 'ok';
  const editable = open && !locked;
  const unlock = useM3Mutation(() => api.post(`/registrations/${x.id}/checklist`, { item: 'comp', status: 'wait' }));

  return (
    <Panel
      flush
      title="Участники"
      count={live.length}
      actions={
        open && locked ? (
          <Button
            small
            disabled={unlock.isPending}
            onClick={() =>
              unlock.mutate(undefined, {
                onSuccess: () => toast.show('Состав разблокирован — строки снова можно править'),
                onError: (e) => toast.showError(errorText(e)),
              })
            }
          >
            Разблокировать состав
          </Button>
        ) : editable ? (
          <>
            {x.team ? (
              <Button small onClick={() => onDialog({ kind: 'pick' })}>
                Из состава команды
              </Button>
            ) : null}
            <Button
              small
              disabled={action.pending}
              onClick={() => action.run(() => api.post(`/registrations/${x.id}/rows`, { count: 1 }), 'Строка добавлена, счёт пересчитан')}
            >
              ＋ Строка
            </Button>
          </>
        ) : undefined
      }
    >
      {x.compWarnings.length ? (
        <Note tone="warn">
          <b>Проверки номинации:</b> {x.compWarnings.join(' · ')}. Это предупреждение — регистрацию оно не блокирует.
        </Note>
      ) : null}
      {open && locked ? (
        <Note>
          Состав утверждён — строки заблокированы: случайная правка не поменяет счёт и проверки. Чтобы поправить,
          разблокируйте его, после правок утвердите снова в чек-листе. Телефоны участников без карточки правятся и так.
        </Note>
      ) : null}
      {x.afterFreeze ? (
        <Note>Дедлайн заморозки прошёл — правки возможны, в ленту они пишутся с пометкой «после заморозки».</Note>
      ) : null}
      {!x.rows.length ? (
        <div className="empty">
          В номере пока нет участников, счёт — 0.{' '}
          {open ? (x.team ? 'Добавьте строки или людей из состава команды.' : 'Добавьте строки.') : null}
        </div>
      ) : (
        <table className="tbl">
          <thead>
            <tr>
              <th className="c-rn">№</th>
              <th>Фамилия</th>
              <th>Имя</th>
              <th>Дата рождения</th>
              <th>Телефон</th>
              <th>Возраст на {dm(x.event.dateFrom)}</th>
              <th>Пакет</th>
              <th className="r">Цена</th>
              <th className="c-ra" />
            </tr>
          </thead>
          <tbody>
            {x.rows.map((r, i) => {
              if (r.withdrawn) {
                return (
                  <tr key={r.id} className="offrow">
                    <td className="rnum">{i + 1}</td>
                    <td data-col="primary">
                      <span className="nm">{r.lastName}</span>
                    </td>
                    <td colSpan={2}>
                      <span>{r.firstName}</span>
                      <div className="sub">
                        снят {dm(r.withdrawn.at)} · {r.withdrawn.reason}
                        {r.withdrawn.by ? ` · ${r.withdrawn.by}` : ''}
                      </div>
                    </td>
                    <td className="num">{r.phone ? <span className="dim">{formatPhone(r.phone)}</span> : <span className="na">—</span>}</td>
                    <td>
                      <span className="na">—</span>
                    </td>
                    <td>
                      <span className="dim">{r.package.name}</span>
                    </td>
                    <td className="r num">
                      <span className="dim">{money(r.price)}</span>
                    </td>
                    <td className="r" data-col="act">
                      {editable ? (
                        <RowActions
                          items={[
                            {
                              label: 'Вернуть участника',
                              onClick: () =>
                                action.run(() => api.post(`/registration-rows/${r.id}/restore`), 'Участник снова в номере, счёт пересчитан'),
                            },
                          ]}
                        />
                      ) : null}
                    </td>
                  </tr>
                );
              }
              const linked = Boolean(r.client);
              const items = editable
                ? [
                    ...(x.team
                      ? [{ label: linked ? 'Заменить другим из состава…' : 'Выбрать из состава команды…', onClick: () => onDialog({ kind: 'pickRow', row: r }) }]
                      : []),
                    ...(linked ? [{ label: 'Отвязать, оставить ФИО', onClick: () => save(r.id, { clientId: null }, 'Строка отвязана от клиента, ФИО и дата остались') }] : []),
                    ...(x.status === 'active' && (r.lastName || r.firstName)
                      ? [{ label: 'Снять участника…', onClick: () => onDialog({ kind: 'withdraw', row: r }) }]
                      : []),
                    { label: 'Убрать строку — заведена по ошибке', danger: true, onClick: () => onDialog({ kind: 'removeRow', row: r }) },
                  ]
                : [];
              return (
                <tr key={r.id}>
                  <td className="rnum">{i + 1}</td>
                  {linked || !editable ? (
                    <>
                      <td data-col="primary">
                        <span className="nm">{r.lastName ?? <span className="na">—</span>}</span>
                      </td>
                      <td>
                        <span className="cwho">
                          <span>{r.firstName}</span>
                          {linked ? <Pill tone="mut">клиент</Pill> : null}
                        </span>
                      </td>
                      <td>{r.birthDate ? <span className="num">{ruDate(r.birthDate)}</span> : <span className="na">—</span>}</td>
                    </>
                  ) : (
                    <>
                      <td data-col="primary">
                        <CellText value={r.lastName ?? ''} placeholder="Фамилия" onSave={(v) => save(r.id, { lastName: v || null })} />
                      </td>
                      <td>
                        <CellText value={r.firstName ?? ''} placeholder="Имя" onSave={(v) => save(r.id, { firstName: v || null })} />
                      </td>
                      <td>
                        <CellText
                          narrow
                          value={r.birthDate ? ruDate(r.birthDate) : ''}
                          placeholder="ДД.ММ.ГГГГ"
                          onSave={(v) => {
                            const m = v.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
                            if (v && !m) return toast.showError('Дата рождения — в формате ДД.ММ.ГГГГ');
                            save(r.id, { birthDate: m ? `${m[3]}-${m[2]}-${m[1]}` : null });
                          }}
                        />
                      </td>
                    </>
                  )}
                  <td>
                    <RowPhone
                      row={r}
                      canEdit={open && !linked}
                      canLink={editable}
                      onSave={(phone) => save(r.id, { phone }, phone ? 'Телефон участника записан' : 'Телефон участника стёрт')}
                      onLink={(clientId) => save(r.id, { clientId }, 'Строка связана с клиентом')}
                    />
                  </td>
                  <td>
                    {r.ageAtEvent === null ? (
                      <span className="na">—</span>
                    ) : (
                      <>
                        <span className={r.outOfAge ? 'age hot' : 'age'}>{years(r.ageAtEvent)}</span>
                        {r.outOfAge ? <span className="wtx sub"> вне возраста</span> : null}
                      </>
                    )}
                  </td>
                  <td>
                    {editable ? (
                      <PopoverSelect
                        value={r.package.id}
                        onChange={(packageId) => save(r.id, { packageId }, 'Пакет участника изменён, счёт пересчитан')}
                        options={x.packages.map((p) => ({
                          value: p.id,
                          label: `${p.name}${p.isDefault ? ' · по умолчанию' : ''}`,
                          side: money(p.price),
                        }))}
                      />
                    ) : (
                      r.package.name
                    )}
                  </td>
                  <td className="r num">{money(r.price)}</td>
                  <td className="r" data-col="act">
                    <RowActions items={items} />
                  </td>
                </tr>
              );
            })}
            <tr className="tot">
              <td colSpan={7}>
                Итого · {people(live.length)}
                {x.withdrawnRows ? ` · снято ${x.withdrawnRows}` : ''}
              </td>
              <td className="r num">{money(sum)}</td>
              <td />
            </tr>
          </tbody>
        </table>
      )}
      <Note>
        Строку можно оставить пустой до заморозки состава, вписать вручную или заполнить из состава команды. В состав
        клиенты добавляются из базы в карточке команды. Человеку во втором и следующих номерах менеджер ставит «Повторное
        участие» — проверка во вкладке раздела «Повторное участие».
      </Note>
    </Panel>
  );
}

function BillTab({ reg: x, onDialog }: { reg: RegistrationCard; onDialog: (d: Dialog) => void }) {
  const off = x.rows.filter((r) => r.withdrawn);
  // скидку при смене состава не пересчитывают — ниже нуля счёт не опускается
  const total = Math.max(0, x.figures.rowsSum - x.figures.discount);
  const def = x.price.defaultPackage;
  return (
    <div className="two">
      <div className="stack">
        <Panel
          flush
          title="Счёт номера"
          actions={x.status === 'active' ? <Button small onClick={() => onDialog({ kind: 'adj' })}>Скидка</Button> : undefined}
        >
          {x.status !== 'active' ? (
            <Note>
              Счёт выставляется только у номера «участвует». {x.status === 'reserve' ? 'При подъёме из резерва он появится.' : ''}
            </Note>
          ) : null}
          <table className="tbl">
            <thead>
              <tr>
                <th>Пакет</th>
                <th className="r">Участников</th>
                <th className="r">Цена</th>
                <th className="r">Сумма</th>
              </tr>
            </thead>
            <tbody>
              {x.bill.map((g) => (
                <tr key={`${g.packageId}:${g.price}`}>
                  <td data-col="primary">
                    {g.name}
                    {g.kind === 'extra' ? <span className="muted"> · дополнительный</span> : null}
                  </td>
                  <td className="r num">{g.count}</td>
                  <td className="r num">{money(g.price)}</td>
                  <td className="r num" data-col="secondary">
                    {money(g.sum)}
                  </td>
                </tr>
              ))}
              {off.length ? (
                <tr>
                  <td data-col="primary">
                    Сняты с номера
                    <div className="sub">
                      {off
                        .map((r) => `${[r.lastName, r.firstName].filter(Boolean).join(' ') || 'без имени'} · ${r.withdrawn!.reason}`)
                        .join('; ')}
                    </div>
                  </td>
                  <td className="r num">{off.length}</td>
                  <td />
                  <td className="r num dim" data-col="secondary">
                    не начисляется
                  </td>
                </tr>
              ) : null}
              {x.figures.discount ? (
                <tr>
                  <td data-col="primary">
                    Скидка{x.figures.discountPct !== null ? ` · ${String(x.figures.discountPct).replace('.', ',')}%` : ''}
                    <div className="sub">{x.figures.discountReason}</div>
                  </td>
                  <td />
                  <td />
                  <td className="r num" data-col="secondary">
                    −{money(x.figures.discount)}
                  </td>
                </tr>
              ) : null}
              <tr className="tot">
                <td colSpan={3}>{x.status === 'active' ? 'Начислено (план)' : 'Было бы начислено'}</td>
                <td className="r num">{money(total)}</td>
              </tr>
            </tbody>
          </table>
          <div className="blk tot m3">
            <div className="kvr2">
              <Kv k="Оплачено (факт)">{money(x.figures.paid)}</Kv>
              <Kv k="Долг">{money(x.figures.debt)}</Kv>
              <Kv k="К возврату">{money(x.figures.toRefund)}</Kv>
            </div>
          </div>
          <Note>
            {x.price.fixed
              ? `Цена зафиксирована первым платежом ${ruDate(x.price.fixedOn)}: ${periodText(x.price.period ?? 1)}. Новые строки и смена пакета считаются по ней же.`
              : `Цена не зафиксирована: сейчас ${periodText(x.price.currentPeriod)}${x.price.currentPeriod === 1 && def?.raiseOn ? `, повышение ${ruDate(def.raiseOn)}` : ''}. Первый платёж любой суммы зафиксирует цену и займёт место.`}
          </Note>
        </Panel>
      </div>
      <div className="stack">
        <Panel
          flush
          title="Платежи и возвраты"
          count={x.money.length}
          actions={x.figures.paid > 0 ? <Button small onClick={() => onDialog({ kind: 'refund' })}>Отметить возврат</Button> : undefined}
        >
          {x.money.length ? (
            <table className="tbl">
              <thead>
                <tr>
                  <th>Дата</th>
                  <th>Операция</th>
                  <th className="r">Сумма</th>
                  <th>Кто</th>
                </tr>
              </thead>
              <tbody>
                {x.money.map((m) => (
                  <tr key={m.id}>
                    <td className="num">{ruDate(m.onDate)}</td>
                    <td data-col="primary">
                      {m.kind === 'payment' ? 'Платёж' : 'Возврат'}
                      <div className="sub">
                        {m.kind === 'payment'
                          ? `${m.method ? METHOD[m.method] : ''}${m.comment ? ` · ${m.comment}` : ''}`
                          : m.reason}
                      </div>
                    </td>
                    <td className="r num" data-col="secondary">
                      {m.kind === 'refund' ? '−' : ''}
                      {money(m.amount)}
                    </td>
                    <td className="muted">{m.by}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <Note>Платежей нет. Сейчас руководитель платит за весь номер по платёжной ссылке, поступление отмечает менеджер.</Note>
          )}
          <Note>Ручная отметка — до модуля «Оплаты». Потом платежи придут из него, логика стадий не изменится.</Note>
        </Panel>
      </div>
    </div>
  );
}

